import React, { useState } from 'react';
import { Banknote, CreditCard, SplitSquareVertical, X, Percent, QrCode, Building2, DollarSign } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const PROMPTPAY_QR = "https://customer-assets.emergentagent.com/job_carib-table-pay/artifacts/jopor950_PHOTO-2026-04-06-21-01-53.jpg";

export const ServerCheckoutOverlay = ({
  order, onClose, onCashPayment, onCardPayment, onSplitPayment,
  onPromptPayPayment, onCounterPayment,
  formatPrice
}) => {
  const [cashAmount, setCashAmount] = useState('');
  const [cashTip, setCashTip] = useState('');
  const [splitPercent, setSplitPercent] = useState('50');
  const [mode, setMode] = useState(null); // null, 'cash', 'split', 'promptpay'
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [showDiscount, setShowDiscount] = useState(false);

  if (!order) return null;

  const afterDiscount = order.total - (order.discount_amount || 0);
  const remaining = Math.max(0, afterDiscount - (order.paid_amount || 0));

  const quickAmounts = [100, 200, 500, 1000];

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      data-testid="server-checkout-overlay">
      <div className="bg-white rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-[#0A422D] text-white p-4 rounded-t-3xl flex items-center justify-between">
          <div>
            <h3 className="font-bold text-lg">Encaissement</h3>
            <p className="text-sm opacity-75">
              Table {order.table_id} {order.order_number ? `| ${order.order_number}` : ''}
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Total */}
        <div className="p-4 border-b border-[#E5DCD3]">
          <div className="flex justify-between items-center">
            <span className="text-[#6E5E52]">Total</span>
            <span className="text-2xl font-bold text-[#2D241D]">{formatPrice(afterDiscount)}</span>
          </div>
          {order.paid_amount > 0 && (
            <div className="flex justify-between items-center mt-1">
              <span className="text-sm text-[#6E5E52]">Deja paye</span>
              <span className="text-sm text-[#2D6A4F]">-{formatPrice(order.paid_amount)}</span>
            </div>
          )}
          {remaining !== afterDiscount && (
            <div className="flex justify-between items-center mt-1 pt-1 border-t border-dashed border-[#E5DCD3]">
              <span className="font-semibold text-[#2D241D]">Reste a payer</span>
              <span className="font-bold text-[#E16833]">{formatPrice(remaining)}</span>
            </div>
          )}
        </div>

        {/* Payment methods - default view */}
        {!mode && (
          <div className="p-4 space-y-3">
            <button onClick={() => setMode('cash')}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#2D6A4F] text-white font-semibold text-sm transition-transform active:scale-[0.98]"
              data-testid="checkout-cash-btn">
              <Banknote className="w-6 h-6" /> <span className="flex-1 text-left">Cash</span>
              <span className="opacity-75">{formatPrice(remaining)}</span>
            </button>
            <button onClick={() => { onCardPayment?.(remaining); onClose(); }}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#6366F1] text-white font-semibold text-sm transition-transform active:scale-[0.98]"
              data-testid="checkout-cb-btn">
              <CreditCard className="w-6 h-6" /> <span className="flex-1 text-left">Carte Bancaire</span>
              <span className="opacity-75">{formatPrice(remaining)}</span>
            </button>
            <button onClick={() => setMode('promptpay')}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#1B3A4B] text-white font-semibold text-sm transition-transform active:scale-[0.98]"
              data-testid="checkout-promptpay-btn">
              <QrCode className="w-6 h-6" /> <span className="flex-1 text-left">PromptPay</span>
              <span className="opacity-75">{formatPrice(remaining)}</span>
            </button>
            <button onClick={() => { onCounterPayment?.(remaining); onClose(); }}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#0A422D] text-white font-semibold text-sm transition-transform active:scale-[0.98]"
              data-testid="checkout-counter-btn">
              <Building2 className="w-6 h-6" /> <span className="flex-1 text-left">Paiement au comptoir</span>
            </button>
            <button onClick={() => setMode('split')}
              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-[#E5DCD3] text-[#2D241D] font-semibold text-sm transition-transform active:scale-[0.98]"
              data-testid="checkout-split-btn">
              <SplitSquareVertical className="w-6 h-6" /> <span className="flex-1 text-left">Split / Diviser</span>
            </button>
            {!showDiscount && (
              <button onClick={() => setShowDiscount(true)}
                className="w-full text-center text-xs text-[#E16833] hover:underline py-1">
                <Percent className="w-3 h-3 inline mr-1" /> Appliquer une remise
              </button>
            )}
            {showDiscount && (
              <div className="bg-[#F3EDE5] rounded-2xl p-3 space-y-2">
                <div className="flex gap-2">
                  <select value={discountType} onChange={(e) => setDiscountType(e.target.value)}
                    className="h-9 rounded-xl border border-[#E5DCD3] px-2 bg-white text-sm flex-1">
                    <option value="percent">%</option>
                    <option value="fixed">THB</option>
                  </select>
                  <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                    placeholder="Valeur" className="rounded-xl border-[#E5DCD3] flex-1" />
                  <Button size="sm" className="bg-[#E16833] text-white rounded-xl"
                    onClick={() => { /* discount logic handled by parent */ }}>
                    OK
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Cash mode */}
        {mode === 'cash' && (
          <div className="p-4 space-y-3">
            <button onClick={() => setMode(null)} className="text-xs text-[#6E5E52] hover:underline flex items-center gap-1">
              <X className="w-3 h-3" /> Retour
            </button>
            <div>
              <label className="text-sm font-medium text-[#2D241D] mb-1 block">Montant recu</label>
              <Input type="number" value={cashAmount} onChange={(e) => setCashAmount(e.target.value)}
                placeholder={String(remaining)} className="rounded-xl border-[#E5DCD3] text-lg font-bold"
                data-testid="cash-amount-input" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {quickAmounts.map(a => (
                <button key={a} onClick={() => setCashAmount(String(a))}
                  className="px-3 py-1.5 rounded-lg bg-[#F3EDE5] text-sm font-medium text-[#2D241D] hover:bg-[#E5DCD3]">
                  {a}
                </button>
              ))}
              <button onClick={() => setCashAmount(String(remaining))}
                className="px-3 py-1.5 rounded-lg bg-[#D4A853]/20 text-sm font-medium text-[#D4A853] hover:bg-[#D4A853]/30">
                Exact
              </button>
            </div>
            <div>
              <label className="text-sm font-medium text-[#2D241D] mb-1 block">Pourboire</label>
              <Input type="number" value={cashTip} onChange={(e) => setCashTip(e.target.value)}
                placeholder="0" className="rounded-xl border-[#E5DCD3]" />
            </div>
            {cashAmount && parseFloat(cashAmount) > remaining && (
              <div className="bg-[#FFF8E1] rounded-xl p-3 text-center">
                <p className="text-sm text-[#D4A853] font-semibold">
                  Monnaie : {formatPrice(parseFloat(cashAmount) - remaining)}
                </p>
              </div>
            )}
            <Button onClick={() => { onCashPayment?.(parseFloat(cashAmount) || remaining, parseFloat(cashTip) || 0); onClose(); }}
              disabled={!cashAmount && remaining > 0}
              className="w-full bg-[#2D6A4F] hover:bg-[#1B4332] text-white rounded-xl h-12 text-base font-bold"
              data-testid="confirm-cash-btn">
              <Banknote className="w-5 h-5 mr-2" /> Valider Cash
            </Button>
          </div>
        )}

        {/* PromptPay mode */}
        {mode === 'promptpay' && (
          <div className="p-4 space-y-3 text-center">
            <button onClick={() => setMode(null)} className="text-xs text-[#6E5E52] hover:underline flex items-center gap-1">
              <X className="w-3 h-3" /> Retour
            </button>
            <p className="text-[#6E5E52] text-sm">
              Montant: <span className="text-[#D4A853] font-bold text-lg">{formatPrice(remaining)}</span>
            </p>
            <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] inline-block mx-auto">
              <img src={PROMPTPAY_QR} alt="PromptPay QR" className="w-48 h-48 object-contain mx-auto"
                data-testid="server-promptpay-qr" />
            </div>
            <p className="text-xs text-[#6E5E52]">Le client scanne ce QR avec son app bancaire</p>
            <Button onClick={() => { onPromptPayPayment?.(remaining); onClose(); }}
              className="w-full bg-[#1B3A4B] hover:bg-[#142D3B] text-white rounded-xl h-12 text-base font-bold"
              data-testid="confirm-promptpay-btn">
              <QrCode className="w-5 h-5 mr-2" /> Paiement recu via PromptPay
            </Button>
          </div>
        )}

        {/* Split mode */}
        {mode === 'split' && (
          <div className="p-4 space-y-3">
            <button onClick={() => setMode(null)} className="text-xs text-[#6E5E52] hover:underline flex items-center gap-1">
              <X className="w-3 h-3" /> Retour
            </button>
            <div>
              <label className="text-sm font-medium text-[#2D241D] mb-1 block">Part a encaisser (%)</label>
              <Input type="number" value={splitPercent} onChange={(e) => setSplitPercent(e.target.value)}
                className="rounded-xl border-[#E5DCD3]" min="1" max="100" />
            </div>
            <div className="flex gap-2">
              {[25, 33, 50, 75].map(p => (
                <button key={p} onClick={() => setSplitPercent(String(p))}
                  className={`flex-1 py-2 rounded-lg text-sm font-medium ${String(p) === splitPercent ? 'bg-[#0A422D] text-white' : 'bg-[#F3EDE5] text-[#2D241D]'}`}>
                  {p}%
                </button>
              ))}
            </div>
            <div className="bg-[#F3EDE5] rounded-xl p-3 text-center">
              <p className="text-sm text-[#6E5E52]">Montant de cette part</p>
              <p className="text-xl font-bold text-[#2D241D]">{formatPrice(remaining * (parseInt(splitPercent) || 50) / 100)}</p>
            </div>
            <Button onClick={() => { onSplitPayment?.(parseInt(splitPercent) || 50); onClose(); }}
              className="w-full bg-[#D4A853] hover:bg-[#C49A45] text-black rounded-xl h-12 text-base font-bold"
              data-testid="confirm-split-btn">
              <SplitSquareVertical className="w-5 h-5 mr-2" /> Encaisser {splitPercent}%
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
